// @refresh skip
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Plus, Trash2, Eye, Languages } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import WysiwygEditor from "@/components/WysiwygEditor";
import { CMSPreview } from "./CMSPreview";
import { useTranslationStatus } from "@/hooks/useTranslationStatus";
import { TranslationStatusBadge } from "./TranslationStatusBadge";

interface PageEditorProps {
  pageKey: string;
  pageTitle: string;
  user: any;
  onRefresh: () => void;
  defaultContent: any;
}

export const PageEditor = ({ pageKey, pageTitle, user, onRefresh, defaultContent }: PageEditorProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorLanguage, setEditorLanguage] = useState<'fr' | 'en' | 'lg-fr' | 'sw'>('fr');
  const [isTranslating, setIsTranslating] = useState(false);
  
  const translationStatus = useTranslationStatus('cms_content', pageKey, editorLanguage);

  useEffect(() => {
    fetchContent();
  }, [pageKey, editorLanguage]);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_content')
        .select('*')
        .eq('key', pageKey)
        .eq('language', editorLanguage)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        const parsedContent = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        setContent({ ...data, content: parsedContent });
      } else {
        // Pas de contenu pour cette langue, essayer de traduire depuis le français
        if (editorLanguage !== 'fr') {
          await translateFromFrench();
        } else {
          await createDefaultContent();
        }
      }
    } catch (error: any) {
      console.error('Error fetching content:', error);
      toast({
        title: t('admin.cms.error'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const translateFromFrench = async (forceRetranslate = false) => {
    try {
      setIsTranslating(true);
      
      // Récupérer le contenu en français
      const { data: frenchData, error: frenchError } = await supabase
        .from('cms_content')
        .select('*')
        .eq('key', pageKey)
        .eq('language', 'fr')
        .maybeSingle();

      if (frenchError || !frenchData) {
        await createDefaultContent();
        return;
      }

      const frenchContent = typeof frenchData.content === 'string' 
        ? JSON.parse(frenchData.content) 
        : frenchData.content;

      toast({
        title: t('admin.cms.translating'),
        description: `${t('admin.cms.translatingTo')} ${editorLanguage}...`,
      });

      const { data: translationData, error: translationError } = await supabase.functions.invoke(
        'translate-cms-content',
        {
          body: {
            content: frenchContent,
            sourceLanguage: 'fr',
            targetLanguage: editorLanguage
          }
        }
      );

      if (translationError) throw translationError;
      if (!translationData?.success) throw new Error('Translation failed');

      // Vérifier si le contenu existe déjà
      const { data: existingContent } = await supabase
        .from('cms_content')
        .select('id')
        .eq('key', pageKey)
        .eq('language', editorLanguage)
        .maybeSingle();

      if (existingContent) {
        const { error: updateError } = await supabase
          .from('cms_content')
          .update({
            content: JSON.stringify(translationData.translatedContent),
            updated_at: new Date().toISOString()
          })
          .eq('key', pageKey)
          .eq('language', editorLanguage);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('cms_content')
          .insert({
            key: pageKey,
            language: editorLanguage,
            title: `${pageTitle} (${editorLanguage})`,
            status: 'published',
            content: JSON.stringify(translationData.translatedContent),
            created_by: user?.id
          });

        if (insertError) throw insertError;
      }

      setContent({ content: translationData.translatedContent, language: editorLanguage });
      toast({
        title: t('admin.cms.success'),
        description: t('admin.cms.translationSuccess'),
      });
      onRefresh();
    } catch (error: any) {
      console.error('Error translating content:', error);
      toast({
        title: t('admin.cms.translationError'),
        description: error.message,
        variant: "destructive"
      });
      if (!forceRetranslate) {
        await createDefaultContent();
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleRetranslate = async () => {
    if (editorLanguage === 'fr') {
      toast({
        title: t('admin.cms.error'),
        description: 'Impossible de retraduire le contenu français',
        variant: "destructive"
      });
      return;
    }
    await translateFromFrench(true);
  };

  const createDefaultContent = async () => {
    try {
      const { data, error } = await supabase
        .from('cms_content')
        .insert({
          key: pageKey,
          title: pageTitle,
          content_type: 'page',
          status: 'published',
          language: editorLanguage,
          content: JSON.stringify(defaultContent),
          created_by: user?.id
        })
        .select()
        .single();

      if (error) throw error;
      setContent({ ...data, content: defaultContent });
      toast({ 
        title: t('admin.cms.success'),
        description: t('admin.cms.saved_description')
      });
    } catch (error: any) {
      console.error('Error creating default content:', error);
      toast({
        title: t('admin.cms.error'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('cms_content')
        .update({
          content: JSON.stringify(content.content),
          updated_at: new Date().toISOString()
        })
        .eq('id', content.id);

      if (error) throw error;

      toast({
        title: t('admin.cms.saved'),
        description: t('admin.cms.saved_description')
      });
      onRefresh();
    } catch (error: any) {
      console.error('Error saving content:', error);
      toast({
        title: t('admin.cms.error'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const updateContent = (path: string, value: any) => {
    const keys = path.split('.');
    const newContentData = { ...content.content };
    let current = newContentData;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setContent({ ...content, content: newContentData });
  };

  const addArrayItem = (path: string, defaultItem: any) => {
    const keys = path.split('.');
    const newContentData = { ...content.content };
    let current = newContentData;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }

    const lastKey = keys[keys.length - 1];
    if (!current[lastKey]) current[lastKey] = [];
    current[lastKey] = [...current[lastKey], defaultItem];
    setContent({ ...content, content: newContentData });
  };

  const removeArrayItem = (path: string, index: number) => {
    const keys = path.split('.');
    const newContentData = { ...content.content };
    let current = newContentData;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }

    const lastKey = keys[keys.length - 1];
    if (Array.isArray(current[lastKey])) {
      current[lastKey] = current[lastKey].filter((_: any, i: number) => i !== index);
    }
    setContent({ ...content, content: newContentData });
  };

  if (!content) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('admin.cms.loading')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const contentData = content.content || {};

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle>{t('admin.cms.editor')} - {pageTitle}</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Langue:</span>
              <select
                value={editorLanguage}
                onChange={(e) => setEditorLanguage(e.target.value as any)}
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="fr">🇫🇷 Français</option>
                <option value="en">🇬🇧 English</option>
                <option value="lg-fr">🇨🇩 Lingala</option>
                <option value="sw">🇹🇿 Swahili</option>
              </select>
            </div>
            {editorLanguage !== 'fr' && (
              <TranslationStatusBadge
                needsUpdate={translationStatus.needsUpdate}
                frenchTimestamp={translationStatus.frenchTimestamp}
                currentTimestamp={translationStatus.currentTimestamp}
                loading={translationStatus.loading}
              />
            )}
          </div>
          <div className="flex gap-2">
            {editorLanguage !== 'fr' && (
              <Button 
                variant="outline" 
                onClick={handleRetranslate}
                disabled={isTranslating}
              >
                <Languages className="h-4 w-4 mr-2" />
                {isTranslating ? 'Traduction...' : 'Retraduire'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewOpen(true)}>
              <Eye className="h-4 w-4 mr-2" />
              {t('admin.cms.preview')}
            </Button>
            <Button onClick={handleSave} disabled={isTranslating}>
              <Save className="h-4 w-4 mr-2" />
              {t('admin.cms.save')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="hero">{t('admin.cms.hero_tab')}</TabsTrigger>
            <TabsTrigger value="sections">{t('admin.cms.sections_tab')}</TabsTrigger>
            {pageKey === 'about-page' && <TabsTrigger value="values">Valeurs & Stats</TabsTrigger>}
            <TabsTrigger value="faq">{t('admin.cms.faq_tab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="hero" className="space-y-4">
            <div className="space-y-4">
              {/* Hero Title */}
              {('hero_title' in defaultContent || contentData.hero_title !== undefined) && (
                <div>
                  <Label>{t('admin.cms.hero_title')}</Label>
                  <Input
                    value={contentData.hero_title || ''}
                    onChange={(e) => updateContent('hero_title', e.target.value)}
                    placeholder={t('admin.cms.hero_title_placeholder')}
                  />
                </div>
              )}
              
              {/* Hero Description */}
              {('hero_description' in defaultContent || contentData.hero_description !== undefined) && (
                <div>
                  <Label>{t('admin.cms.hero_description')}</Label>
                  <WysiwygEditor
                    value={contentData.hero_description || ''}
                    onChange={(value) => updateContent('hero_description', value)}
                    placeholder={t('admin.cms.hero_description_placeholder')}
                  />
                </div>
              )}

              {/* Champs personnalisés du hero */}
              {Object.keys(defaultContent).filter(key => 
                key.startsWith('hero_') && 
                key !== 'hero_title' && 
                key !== 'hero_description'
              ).map((key) => (
                <div key={key}>
                  <Label className="capitalize">
                    {key.replace('hero_', '').replace(/_/g, ' ')}
                  </Label>
                  {typeof defaultContent[key] === 'string' && defaultContent[key].length > 100 ? (
                    <WysiwygEditor
                      value={contentData[key] || ''}
                      onChange={(value) => updateContent(key, value)}
                      placeholder={`Entrez ${key.replace('hero_', '').replace(/_/g, ' ')}`}
                    />
                  ) : (
                    <Input
                      value={contentData[key] || ''}
                      onChange={(e) => updateContent(key, e.target.value)}
                      placeholder={`Entrez ${key.replace('hero_', '').replace(/_/g, ' ')}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="sections" className="space-y-4">
            <div className="space-y-6">
              {/* Sections title + content détectées automatiquement */}
              {Object.keys(defaultContent).filter(key => key.endsWith('_title') && !key.startsWith('hero')).length > 0 ? (
                Object.keys(defaultContent).filter(key => key.endsWith('_title') && !key.startsWith('hero')).map((titleKey) => {
                  const baseKey = titleKey.replace('_title', '');
                  const contentKey = `${baseKey}_content`;
                  
                  return (
                    <Card key={titleKey}>
                      <CardHeader>
                        <CardTitle className="text-base capitalize">
                          {baseKey.replace(/_/g, ' ')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>{t('admin.cms.section_title')}</Label>
                          <Input
                            value={contentData[titleKey] || defaultContent[titleKey] || ''}
                            onChange={(e) => updateContent(titleKey, e.target.value)}
                            placeholder={t('admin.cms.section_title_placeholder')}
                          />
                        </div>
                        <div>
                          <Label>{t('admin.cms.section_content')}</Label>
                          <WysiwygEditor
                            value={contentData[contentKey] || defaultContent[contentKey] || ''}
                            onChange={(value) => updateContent(contentKey, value)}
                            placeholder={t('admin.cms.section_content_placeholder')}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Aucune section définie pour cette page.
                </p>
              )}
              
              {/* Autres champs personnalisés (ni hero, ni _title/_content, ni faq) */}
              {Object.keys(defaultContent).filter(key => 
                !key.startsWith('hero') && 
                !key.endsWith('_title') && 
                !key.endsWith('_content') && 
                key !== 'faq' &&
                typeof defaultContent[key] === 'string'
              ).map((key) => (
                <Card key={key}>
                  <CardHeader>
                    <CardTitle className="text-base capitalize">{key.replace(/_/g, ' ')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                      <WysiwygEditor
                        value={contentData[key] || defaultContent[key] || ''}
                        onChange={(value) => updateContent(key, value)}
                        placeholder={`Entrez le contenu pour ${key.replace(/_/g, ' ')}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {pageKey === 'about-page' && (
            <TabsContent value="values" className="space-y-4">
              <div className="space-y-6">
                {/* Titre de la section valeurs */}
                <div>
                  <Label>Titre de la section</Label>
                  <Input
                    value={contentData.values_title || ''}
                    onChange={(e) => updateContent('values_title', e.target.value)}
                    placeholder="Nos Valeurs"
                  />
                </div>

                {/* Valeurs */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-base">Valeurs</CardTitle>
                      <Button
                        size="sm"
                        onClick={() => {
                          const currentValues = contentData.values || [];
                          const newValues = [...currentValues, { icon: 'shield', title: '', description: '' }];
                          updateContent('values', newValues);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une valeur
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(contentData.values || []).map((value: any, index: number) => (
                      <Card key={index}>
                        <CardContent className="pt-6 space-y-4">
                          <div className="flex justify-between items-start">
                            <Label>Valeur {index + 1}</Label>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const newValues = contentData.values.filter((_: any, i: number) => i !== index);
                                updateContent('values', newValues);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div>
                            <Label>Icône</Label>
                            <select
                              value={value.icon || 'shield'}
                              onChange={(e) => {
                                const newValues = [...contentData.values];
                                newValues[index] = { ...newValues[index], icon: e.target.value };
                                updateContent('values', newValues);
                              }}
                              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                            >
                              <option value="shield">🛡️ Shield (Sécurité)</option>
                              <option value="users">👥 Users (Communauté)</option>
                              <option value="package">📦 Package (Fiabilité)</option>
                              <option value="trending-up">📈 Trending Up (Innovation)</option>
                            </select>
                          </div>

                          <div>
                            <Label>Titre</Label>
                            <Input
                              value={value.title || ''}
                              onChange={(e) => {
                                const newValues = [...contentData.values];
                                newValues[index] = { ...newValues[index], title: e.target.value };
                                updateContent('values', newValues);
                              }}
                              placeholder="Ex: Sécurité"
                            />
                          </div>

                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={value.description || ''}
                              onChange={(e) => {
                                const newValues = [...contentData.values];
                                newValues[index] = { ...newValues[index], description: e.target.value };
                                updateContent('values', newValues);
                              }}
                              placeholder="Description de la valeur"
                              rows={3}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {(!contentData.values || contentData.values.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">
                        Aucune valeur ajoutée. Cliquez sur "Ajouter une valeur" pour commencer.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Statistiques */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Statistiques</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre de vendeurs</Label>
                        <Input
                          value={contentData.stats?.vendors || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), vendors: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="1000+"
                        />
                      </div>
                      <div>
                        <Label>Label vendeurs</Label>
                        <Input
                          value={contentData.stats?.vendors_label || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), vendors_label: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="Vendeurs Actifs"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre de clients</Label>
                        <Input
                          value={contentData.stats?.customers || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), customers: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="10,000+"
                        />
                      </div>
                      <div>
                        <Label>Label clients</Label>
                        <Input
                          value={contentData.stats?.customers_label || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), customers_label: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="Clients Satisfaits"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nombre de points relais</Label>
                        <Input
                          value={contentData.stats?.relays || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), relays: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="50+"
                        />
                      </div>
                      <div>
                        <Label>Label points relais</Label>
                        <Input
                          value={contentData.stats?.relays_label || ''}
                          onChange={(e) => {
                            const newStats = { ...(contentData.stats || {}), relays_label: e.target.value };
                            updateContent('stats', newStats);
                          }}
                          placeholder="Points Relais"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          <TabsContent value="faq" className="space-y-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label>{t('admin.cms.faq_title')}</Label>
                <Button
                  size="sm"
                  onClick={() => {
                    const currentFaq = contentData.faq || [];
                    const newFaq = [...currentFaq, { question: '', answer: '' }];
                    updateContent('faq', newFaq);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('admin.cms.add_faq')}
                </Button>
              </div>

              {(() => {
                const faqData = contentData.faq || [];
                
                if (!Array.isArray(faqData) || faqData.length === 0) return null;
                
                return faqData.map((item: any, index: number) => (
                  <Card key={index}>
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex justify-between items-start">
                        <Label>{t('admin.cms.question')} {index + 1}</Label>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const newFaq = contentData.faq.filter((_: any, i: number) => i !== index);
                            updateContent('faq', newFaq);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={item.question || ''}
                        onChange={(e) => {
                          const newFaq = [...contentData.faq];
                          newFaq[index] = { ...newFaq[index], question: e.target.value };
                          updateContent('faq', newFaq);
                        }}
                        placeholder={t('admin.cms.question_placeholder')}
                      />
                      <WysiwygEditor
                        value={item.answer || ''}
                        onChange={(value) => {
                          const newFaq = [...contentData.faq];
                          newFaq[index] = { ...newFaq[index], answer: value };
                          updateContent('faq', newFaq);
                        }}
                        placeholder={t('admin.cms.answer_placeholder')}
                      />
                    </CardContent>
                  </Card>
                ));
              })()}

              {(!contentData.faq || contentData.faq.length === 0) && (
                <p className="text-center text-muted-foreground py-8">
                  {t('admin.cms.no_faq', 'Aucune question ajoutée. Cliquez sur "Ajouter une question" pour commencer.')}
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <CMSPreview 
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        content={contentData}
        pageKey={pageKey}
      />
    </Card>
  );
};
